import { TGlobalStores } from '@/editor/stores';
import { RoomBaseData, RoomData } from '../data/room/Room';
import { modelManager } from '../manager/entityManager';
import { IWindowData } from '../data/model/model';
import { EOperationType } from '../types/operation';
import { confirm, showToast } from '@/editor/utils/feedback';
import i18n from '@/i18n';
import editorEvent, { EditorEventName } from '../event/editorEvent';
import { EFaceType } from '../entities/type';
import { getRoomRelateWallIds } from '../data/room/utils';

export class RoomOperation {
  private _wallStore: TGlobalStores['wallStore'];
  private _separateWallStore: TGlobalStores['separateWallStore'];
  private _roomStore: TGlobalStores['roomStore'];
  private _regionStore: TGlobalStores['regionStore'];
  private _gs: TGlobalStores;

  constructor(gs: TGlobalStores) {
    this._gs = gs;
    this._wallStore = gs.wallStore;
    this._separateWallStore = gs.separateWallStore;
    this._roomStore = gs.roomStore;
    this._regionStore = gs.regionStore;
  }

  private $history_delRoom({
    removeWallIds,
    removeSeparateWallIds
  }: {
    removeWallIds: string[];
    removeSeparateWallIds: string[];
  }) {
    this._gs.history.startOperation();

    // 删除关联门窗
    const relativeInsertElements: IWindowData[] = [];
    for (const wallId of removeWallIds) {
      const relativeWindowDatas = this._gs.modelStore.getWindowsByWallId(wallId);
      relativeInsertElements.push(...relativeWindowDatas);
    }
    for (const separateWallId of removeSeparateWallIds) {
      const relativeWindowDatas = this._gs.modelStore.getWindowsByWallId(separateWallId);
      relativeInsertElements.push(...relativeWindowDatas);
    }
    relativeInsertElements.forEach((windowData) => {
      this._gs.modelStore.removeWindowData(windowData.id);
    });

    // 删除隔墙
    for (const separateWallId of removeSeparateWallIds) {
      this._gs.separateWallStore.removeSepData(separateWallId, false);
    }

    // 删除墙
    for (const wallId of removeWallIds) {
      this._gs.wallStore.removeWallDataById(wallId);
    }

    this._gs.refreshData();

    // 将操作记录压栈
    this._gs.history.completeOperation(EOperationType.RoomDel);
  }

  private $history_modifyRoomBaseData(roomId: string, newData: DeepPartial<RoomBaseData>) {
    const currentRoom = this._gs.roomStore.getRoomData(roomId);
    if (!currentRoom) return;

    if (!Object.keys(newData).length) return;

    this._gs.history.startOperation();

    this._gs.roomStore.updateRoomBaseData(roomId, newData);

    modelManager.textInfoManager.updateSingleRoom(currentRoom.id);
    // 将操作记录压栈
    this._gs.history.completeOperation(EOperationType.RoomModify);
  }

  modifyMaterial(roomId: string, materialId: string) {
    const regionData = this._roomStore.getRoomData(roomId);
    if (!regionData) return;

    this.$history_modifyRoomBaseData(roomId, {
      materialInfo: { id: materialId }
    });

    modelManager.roomManager.updateEntity(roomId);
    return true;
  }

  /**
   * 删除房间
   */
  async deleteRoomByRoomId(id: string) {
    const roomData = this._roomStore.getRoomData(id);
    if (!roomData) return;

    const { removeWallIds, removeSeparateWallIds } = this.getRemoveWalls(roomData);

    if (!removeWallIds.length) {
      showToast(i18n.t('draw.toast.room.has_shared_walls'));
      return;
    }

    const roomRegions = this._regionStore.getRoomRegions(id);
    const withRegions = roomRegions.length > 1;
    const confirmed = withRegions ? await confirm({ content: i18n.t('draw.delete.confirm.room') }) : true;

    if (!confirmed) return;

    if (!removeWallIds.length) {
      return;
    }

    this.$history_delRoom({
      removeWallIds,
      removeSeparateWallIds
    });

    modelManager.updateEntity();
  }

  private getRemoveWalls(roomData: RoomData) {
    const roomDatas = this._roomStore.getRoomDatas();

    const removeWallIds = [];
    const relateWallIds = getRoomRelateWallIds(roomData);
    for (const rWallDataId of relateWallIds) {
      const rWallData = this._wallStore.getWallData(rWallDataId);

      if (rWallData) {
        let isCoWall = false; // 是否与多个房间有公共墙

        // let canRemoveWall = true;
        for (const rm of roomDatas) {
          if (rm.id === roomData.id) continue;

          const relateWallId = getRoomRelateWallIds(rm).find((rwId) => rWallDataId === rwId);
          if (relateWallId) {
            isCoWall = true;
            break;
          }
        }
        if (!isCoWall) {
          removeWallIds.push(rWallDataId);
        }
      }
    }

    const removeSeparateWallIds = this._separateWallStore
      .getSepDatas({ roomIds: [roomData.id], onlyNotBase: true })
      .map((swd) => swd.id);

    // if (canRemoveRoom) {
    //   const index = roomDatas.findIndex(rd => equalsRoomData(rd, roomData));
    //   roomDatas.splice(index, 1);
    // }

    return { removeWallIds, removeSeparateWallIds };
  }

  modifyRoomBaseData(roomId: string, newData: DeepPartial<RoomBaseData>) {
    this.$history_modifyRoomBaseData(roomId, newData);
  }

  private $history_clearRoom(roomData: RoomData) {
    this._gs.history.startOperation();
    // 清空房间内的家具、门窗、隔墙等元素
    this.dangerouslyClearRoom(roomData);

    this._gs.history.completeOperation(EOperationType.ClearArea);
  }

  /**
   * 清除房间内的隔墙、家具、门窗
   */
  dangerouslyClearRoom(roomId: string): void;
  dangerouslyClearRoom(roomData: RoomData): void;
  dangerouslyClearRoom(roomDataOrId: RoomData | string): void {
    const roomData = typeof roomDataOrId === 'string' ? this._roomStore.getRoomData(roomDataOrId) : roomDataOrId;
    if (!roomData) return;

    const { removeSeparateWallIds } = this.getRemoveWalls(roomData);
    const furnitureInRoom = this._gs.modelStore.getFurnitureInPathInfos(roomData.pathInfos, roomData.holeInfos);

    const assemblySet = new Set<string>();
    furnitureInRoom.forEach((furniture) => {
      const assemblyId = this._gs.assemblyStore.getAssemblyIdByFunitureId(furniture.id);
      if (assemblyId) assemblySet.add(assemblyId);
      this._gs.modelStore.removeFurnitureData(furniture.id);
    });

    for (const assemblyId of assemblySet.values()) {
      this._gs.assemblyOperation.refreshAssembly(assemblyId);
    }

    // 删除隔墙的门窗
    const removedDoorWindows: IWindowData[] = [];
    for (const separateWallId of removeSeparateWallIds) {
      const relativeWindowDatas = this._gs.modelStore.getWindowsByWallId(separateWallId);
      removedDoorWindows.push(...relativeWindowDatas);
    }

    removedDoorWindows.forEach((windowData) => {
      this._gs.modelStore.removeWindowData(windowData.id);
    });

    // 删除隔墙
    removeSeparateWallIds.forEach((separateWallId) => {
      this._gs.separateWallStore.removeSepData(separateWallId, false);
    });

    // 重置房间材质
    this._gs.roomStore.updateBaseData(roomData.id, {
      materialInfo: this._gs.getDefaultMaterial({ type: EFaceType.FloorFace })
    });

    const roomRegionIds = this._regionStore.getRoomRegions(roomData.id).map((r) => r.id);
    // 重置墙体材质
    this._gs.faceStore
      .getSepWallFaceDatas()
      .filter((fd) => roomRegionIds.includes(fd.relativeInfo.regionId ?? ''))
      .forEach((fd) => {
        this._gs.faceStore.updateFaceMaterial(fd.id, this._gs.getDefaultMaterial({ type: EFaceType.WallFace }));
      });

    // 重置柱面材质
    const columnIds = this._gs.structStore
      .getColumnDatasInPathInfos(roomData.corePathInfos, roomData.coreHoleInfos, 'center')
      .map((column) => column.id);

    this._gs.faceStore
      .getColumnFaceDatas()
      .filter(
        (faceData) => faceData.relativeInfo.columnId !== undefined && columnIds.includes(faceData.relativeInfo.columnId)
      )
      .forEach((faceData) => {
        this._gs.faceStore.updateFaceMaterial(faceData.id, this._gs.getDefaultMaterial({ type: EFaceType.ColumnFace }));
      });

    this._gs.refreshByRoomId(roomData.id);
  }

  async clearRoom(roomId: string) {
    const roomData = this._roomStore.getRoomData(roomId);
    if (!roomData) return;

    const confirmed = await confirm({
      content: i18n.t('draw.clear.confirm.room')
    });

    if (!confirmed) return;

    editorEvent.emit(EditorEventName.REPLACE_END);
    this.$history_clearRoom(roomData);
    modelManager.updateEntity();
  }
}
